import { useCallback, useEffect, useState, lazy, Suspense } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, Trash2, Sheet, ExternalLink } from "lucide-react";
import { adminLogin, fetchLeads, setLeadStatus, removeLead, getContent, saveContent, fetchStories, addStory, removeStory, exportLeadsCsv, fetchAllReviews, setReviewStatus, removeReview, fetchSheetsStatus, sheetsConnectUrl, formatApiError, inr, fetchAccessMe, mfaSetupBegin, mfaSetupVerify, mfaVerify } from "@/lib/api";
import { Logo } from "@/components/landing/Header";
import { PackageManager } from "@/components/admin/PackageManager";
import { FareManager } from "@/components/admin/FareManager";
import { FeedbackManager } from "@/components/admin/FeedbackManager";
import { WhatsAppInbox } from "@/components/admin/WhatsAppInbox";
import { AssociatesManager } from "@/components/admin/AssociatesManager";
import { AccessManager } from "@/components/admin/AccessManager";
import { SalesToursManager } from "@/components/admin/SalesToursManager";
import { FlightsManager } from "@/components/admin/FlightsManager";
import { ReelsModeration } from "@/components/admin/ReelsModeration";
import { TabErrorBoundary } from "@/components/admin/TabErrorBoundary";

// Lazy-loaded so a stale/corrupt social bundle can never blank the whole admin
// portal — the error boundary shows a retry panel for that tab only.
const SocialMediaManager = lazy(() =>
    import("@/components/admin/SocialMediaManager").then((m) => ({ default: m.SocialMediaManager }))
);

const TOKEN_KEY = "gc_admin_token";

function LoginCard({ onLogin }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    // MFA stages: credentials → setup (first login) / mfa (returning) → backup (codes shown once)
    const [stage, setStage] = useState("credentials");
    const [mfaToken, setMfaToken] = useState("");
    const [setup, setSetup] = useState(null); // {secret, qr}
    const [code, setCode] = useState("");
    const [backupCodes, setBackupCodes] = useState(null);

    const beginSetup = async (mToken) => {
        const s = await mfaSetupBegin(mToken);
        setSetup(s);
        setStage("setup");
    };

    const finish = (data) => {
        localStorage.setItem(TOKEN_KEY, data.access_token);
        onLogin(data.access_token);
    };

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const data = await adminLogin(email, password);
            if (data.access_token) return finish(data);
            setMfaToken(data.mfa_token);
            if (data.mfa_setup_required) await beginSetup(data.mfa_token);
            else setStage("mfa");
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    const submitCode = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const data = stage === "setup" ? await mfaSetupVerify(mfaToken, code) : await mfaVerify(mfaToken, code);
            if (data.backup_codes) {
                setBackupCodes(data.backup_codes);
                localStorage.setItem(TOKEN_KEY, data.access_token);
                setStage("backup");
            } else {
                finish(data);
            }
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    if (stage === "backup" && backupCodes) {
        return (
            <div className="min-h-screen bg-navy-deep flex items-center justify-center px-4" data-testid="admin-login-page">
                <div className="w-full max-w-sm bg-cream rounded-2xl p-8 border border-stone-300" data-testid="mfa-backup-codes">
                    <Logo />
                    <h1 className="mt-6 font-display font-black text-xl text-navy">Save your recovery codes</h1>
                    <p className="text-xs text-stone-500 mt-1 mb-4">Each code works once if you lose your phone. Store them somewhere safe — they are shown only now.</p>
                    <div className="grid grid-cols-2 gap-2 rounded-xl border border-stone-300 bg-white p-4">
                        {backupCodes.map((c) => <code key={c} className="text-center text-sm font-bold text-navy tracking-widest" data-testid="backup-code">{c}</code>)}
                    </div>
                    <button onClick={() => navigator.clipboard.writeText(backupCodes.join("\n")).then(() => toast.success("Recovery codes copied"))}
                        data-testid="backup-codes-copy" className="mt-3 w-full rounded-full border border-stone-300 text-navy text-xs font-black uppercase tracking-wider py-2.5 hover:border-navy">Copy codes</button>
                    <button onClick={() => onLogin(localStorage.getItem(TOKEN_KEY))} data-testid="mfa-finish-btn"
                        className="mt-3 w-full rounded-full bg-saffron hover:bg-saffron-dark text-white font-display font-black text-sm uppercase tracking-wider py-3">I've saved them — continue</button>
                </div>
            </div>
        );
    }

    if (stage === "setup" || stage === "mfa") {
        return (
            <div className="min-h-screen bg-navy-deep flex items-center justify-center px-4" data-testid="admin-login-page">
                <form onSubmit={submitCode} className="w-full max-w-sm bg-cream rounded-2xl p-8 border border-stone-300" data-testid="mfa-form">
                    <Logo />
                    {stage === "setup" ? (
                        <>
                            <h1 className="mt-6 font-display font-black text-xl text-navy">Set up two-factor security</h1>
                            <p className="text-xs text-stone-500 mt-1 mb-4">Required for all admin accounts. Scan with Google/Microsoft Authenticator (or any TOTP app), then enter the 6-digit code.</p>
                            {setup && (
                                <div className="rounded-xl border border-stone-300 bg-white p-4 text-center">
                                    <img src={setup.qr} alt="MFA QR code" className="mx-auto h-40 w-40" data-testid="mfa-qr" />
                                    <p className="mt-2 text-[11px] text-stone-500">Can't scan? Enter this key manually:</p>
                                    <code className="text-xs font-bold text-navy tracking-widest break-all" data-testid="mfa-secret">{setup.secret}</code>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <h1 className="mt-6 font-display font-black text-xl text-navy">Two-factor check</h1>
                            <p className="text-xs text-stone-500 mt-1 mb-4">Enter the 6-digit code from your authenticator app, or a recovery code.</p>
                        </>
                    )}
                    {error && <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" data-testid="mfa-error">{error}</div>}
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-1">Authentication code</label>
                    <input inputMode="numeric" autoComplete="one-time-code" data-testid="mfa-code-input" className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-center text-lg font-bold tracking-[0.4em] outline-none focus:border-saffron" value={code} onChange={(e) => setCode(e.target.value.trim())} required />
                    <button type="submit" disabled={loading} data-testid="mfa-submit" className="mt-5 w-full rounded-full bg-saffron hover:bg-saffron-dark text-white font-display font-black text-sm uppercase tracking-wider py-3 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                        {loading && <Loader2 className="h-4 w-4 animate-spin" />} Verify & continue
                    </button>
                    <button type="button" onClick={() => { setStage("credentials"); setCode(""); setError(""); }} data-testid="mfa-back" className="mt-3 w-full text-xs font-bold text-stone-400 hover:text-navy">← Back to login</button>
                </form>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-navy-deep flex items-center justify-center px-4" data-testid="admin-login-page">
            <form onSubmit={submit} className="w-full max-w-sm bg-cream rounded-2xl p-8 border border-stone-300" data-testid="admin-login-form">
                <Logo />
                <h1 className="mt-6 font-display font-black text-xl text-navy">Admin Login</h1>
                <p className="text-xs text-stone-500 mt-1 mb-6">Enquiries & site content dashboard</p>
                {error && <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" data-testid="admin-login-error">{error}</div>}
                <label className="block text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-1">Email</label>
                <input type="email" data-testid="admin-email-input" className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-saffron" value={email} onChange={(e) => setEmail(e.target.value)} required />
                <label className="block text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-1 mt-4">Password</label>
                <input type="password" data-testid="admin-password-input" className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-saffron" value={password} onChange={(e) => setPassword(e.target.value)} required />
                <button type="submit" disabled={loading} data-testid="admin-login-submit" className="mt-6 w-full rounded-full bg-saffron hover:bg-saffron-dark text-white font-display font-black text-sm uppercase tracking-wider py-3 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                    {loading && <Loader2 className="h-4 w-4 animate-spin" />} Login
                </button>
            </form>
        </div>
    );
}

const STATUS_STYLES = {
    new: "bg-gold/20 text-yellow-700",
    contacted: "bg-blue-100 text-blue-700",
    closed: "bg-green-100 text-green-700",
};

export default function AdminPage({ initialView = "dashboard" }) {
    const [view, setView] = useState(initialView);
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
    const [access, setAccess] = useState(null); // {is_super, permissions}
    const [leads, setLeads] = useState([]);
    const [stories, setStories] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [sheets, setSheets] = useState(null);
    const [storyForm, setStoryForm] = useState({ title: "", travellers: "", quote: "", name: "", org: "", image: "" });
    const [addingStory, setAddingStory] = useState(false);
    const [stats, setStats] = useState({ years: "", trips: "", travellers: "", coverage: "" });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const canDashboard = !!access && (access.is_super || (access.permissions || []).includes("dashboard"));

    const load = useCallback(async (t) => {
        setLoading(true);
        try {
            const [l, c, s, rv, sh] = await Promise.all([fetchLeads(t), getContent(), fetchStories(), fetchAllReviews(t), fetchSheetsStatus(t)]);
            setLeads(l);
            setStories(s);
            setReviews(rv);
            setSheets(sh);
            if (c?.stats) setStats(c.stats);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem(TOKEN_KEY);
                setToken("");
            } else if (err.response?.status !== 403) {
                toast.error("Failed to load dashboard data.");
            }
        } finally {
            setLoading(false);
        }
    }, []);

    // who am I + what can I see
    useEffect(() => {
        if (!token) return;
        (async () => {
            try { setAccess(await fetchAccessMe(token)); }
            catch { localStorage.removeItem(TOKEN_KEY); setToken(""); }
        })();
    }, [token]);

    useEffect(() => { if (token && access && canDashboard) load(token); else setLoading(false); }, [token, access, canDashboard, load]);

    const ALL_TABS = [["dashboard", "Dashboard"], ["packages", "Packages"], ["fares", "Fares & Pricing"], ["sales", "Sales Tours"], ["flights", "Flights"], ["social", "Social Media"], ["reels", "Reels"], ["feedback", "Feedback"], ["inbox", "Inbox"], ["associates", "Associates"]];
    const allowedTabs = !access ? []
        : [...ALL_TABS.filter(([k]) => access.is_super || (access.permissions || []).includes(k)),
           ...(access.is_super ? [["access", "Access"]] : [])];

    // keep the current view within what this user may see
    useEffect(() => {
        if (allowedTabs.length && !allowedTabs.some(([k]) => k === view)) setView(allowedTabs[0][0]);
    }, [allowedTabs, view]);

    useEffect(() => {
        const p = new URLSearchParams(window.location.search).get("sheets");
        if (p === "connected") {
            toast.success("Google Sheet connected! Every new enquiry now lands in your master sheet.");
            window.history.replaceState({}, "", "/admin");
        } else if (p === "error") {
            toast.error("Google connection didn't complete — please click Connect again and finish the Allow step without refreshing or going back.");
            window.history.replaceState({}, "", "/admin");
        }
    }, []);

    if (!token) return <LoginCard onLogin={setToken} />;
    if (!access) return <div className="min-h-screen bg-cream flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-saffron" /></div>;

    const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(""); };

    const changeStatus = async (id, status) => {
        try {
            await setLeadStatus(token, id, status);
            setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
        } catch { toast.error("Status update failed"); }
    };

    const del = async (id) => {
        try {
            await removeLead(token, id);
            setLeads((ls) => ls.filter((l) => l.id !== id));
            toast.success("Lead deleted");
        } catch { toast.error("Delete failed"); }
    };

    const saveStats = async () => {
        setSaving(true);
        try {
            await saveContent(token, stats);
            toast.success("Homepage stats updated");
        } catch { toast.error("Could not save stats"); }
        finally { setSaving(false); }
    };

    const submitStory = async (e) => {
        e.preventDefault();
        if (!storyForm.title.trim() || !storyForm.quote.trim() || !storyForm.name.trim()) {
            toast.error("Title, quote and client name are required.");
            return;
        }
        setAddingStory(true);
        try {
            const s = await addStory(token, storyForm);
            setStories((list) => [s, ...list]);
            setStoryForm({ title: "", travellers: "", quote: "", name: "", org: "", image: "" });
            toast.success("Story published to the homepage");
        } catch { toast.error("Could not add story"); }
        finally { setAddingStory(false); }
    };

    const delStory = async (id) => {
        try {
            await removeStory(token, id);
            setStories((list) => list.filter((s) => s.id !== id));
            toast.success("Story removed");
        } catch { toast.error("Delete failed"); }
    };

    const reviewAction = async (id, status) => {
        try {
            if (status === "delete") {
                await removeReview(token, id);
                setReviews((list) => list.filter((r) => r.id !== id));
                toast.success("Review deleted");
            } else {
                await setReviewStatus(token, id, status);
                setReviews((list) => list.map((r) => (r.id === id ? { ...r, status } : r)));
                toast.success(status === "approved" ? "Review is live on the wall" : "Review rejected");
            }
        } catch { toast.error("Action failed"); }
    };

    return (
        <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
            <header className="bg-navy-deep text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <Logo light />
                    <div className="flex items-center gap-2">
                        <button onClick={() => load(token)} data-testid="admin-refresh-btn" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-full border border-white/20 hover:bg-white/10">
                            <RefreshCw className="h-3.5 w-3.5" /> Refresh
                        </button>
                        <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-full bg-saffron hover:bg-saffron-dark">
                            <LogOut className="h-3.5 w-3.5" /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 flex gap-2 flex-wrap" data-testid="admin-tabs">
                {allowedTabs.map(([k, label]) => (
                    <button key={k} onClick={() => setView(k)} data-testid={`admin-tab-${k}`}
                        className={`rounded-full px-5 py-2.5 text-xs font-display font-black uppercase tracking-wider transition-colors ${view === k ? "bg-navy text-white shadow-[3px_3px_0_0_#F26522]" : "bg-white text-navy border border-stone-300 hover:border-navy"}`}>
                        {label}
                    </button>
                ))}
            </div>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
                {view === "dashboard" ? (<>
                <section className="bg-white rounded-2xl border border-stone-300 p-6" data-testid="sheets-section">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div>
                            <h2 className="font-display font-black text-lg text-navy flex items-center gap-2"><Sheet className="h-5 w-5 text-green-700" /> Google Sheet — Live Master Leads</h2>
                            <p className="text-xs text-stone-500 mt-1">
                                {sheets?.connected
                                    ? "Every new enquiry is appended live. Your team can manage leads right inside the sheet — Status syncs both ways."
                                    : "Connect ghumnechaleyholidays@gmail.com once — a 'GhumneChaley Master Leads' sheet is auto-created and every enquiry lands there live."}
                            </p>
                        </div>
                        {sheets?.connected ? (
                            <a href={sheets.url} target="_blank" rel="noreferrer" data-testid="sheets-open-btn" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-green-700 text-white text-xs font-display font-black uppercase tracking-wider hover:bg-green-800">
                                Open Master Sheet <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                        ) : (
                            <a href={sheetsConnectUrl(token)} data-testid="sheets-connect-btn" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-green-700 text-white text-xs font-display font-black uppercase tracking-wider hover:bg-green-800">
                                <Sheet className="h-3.5 w-3.5" /> Connect Google Sheet
                            </a>
                        )}
                    </div>
                </section>

                <section className="bg-white rounded-2xl border border-stone-300 p-6" data-testid="stats-editor">
                    <h2 className="font-display font-black text-lg text-navy">Homepage Stats (editable)</h2>
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[["years", "Years"], ["trips", "Trips"], ["travellers", "Travellers"], ["coverage", "Coverage"]].map(([k, label]) => (
                            <div key={k}>
                                <label className="block text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-1">{label}</label>
                                <input data-testid={`stats-${k}-input`} className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-saffron" value={stats[k] || ""} onChange={(e) => setStats((s) => ({ ...s, [k]: e.target.value }))} />
                            </div>
                        ))}
                    </div>
                    <button onClick={saveStats} disabled={saving} data-testid="stats-save-btn" className="mt-4 px-6 py-2.5 rounded-full bg-navy text-white text-xs font-display font-black uppercase tracking-wider hover:bg-navy-light disabled:opacity-60">
                        {saving ? "Saving…" : "Save Stats"}
                    </button>
                </section>

                <section className="bg-white rounded-2xl border border-stone-300 p-6" data-testid="reviews-manager">
                    <h2 className="font-display font-black text-lg text-navy">
                        Customer Reviews <span className="text-saffron">({reviews.filter((r) => r.status === "pending").length} pending)</span>
                    </h2>
                    <p className="text-xs text-stone-500 mt-1">
                        Customers submit at <span className="font-bold text-navy">/review</span> — approved reviews appear on the homepage wall before the starter set.
                    </p>
                    {reviews.length === 0 ? (
                        <p className="py-6 text-sm text-stone-500" data-testid="reviews-empty">No customer reviews yet. Share yoursite.com/review with travellers after each trip.</p>
                    ) : (
                        <ul className="mt-4 space-y-3">
                            {reviews.map((r) => (
                                <li key={r.id} className="rounded-xl border border-stone-200 p-4" data-testid={`review-row-${r.id}`}>
                                    <div className="flex flex-wrap items-center gap-2 text-xs">
                                        <span className="font-black text-navy text-sm">{r.name}</span>
                                        <span className="text-stone-500">{r.city}</span>
                                        <span className="text-gold font-black">{"★".repeat(r.rating)}</span>
                                        {r.trip_type && <span className="bg-saffron/10 text-saffron rounded-full px-2 py-0.5 font-bold uppercase tracking-wider text-[10px]">{r.trip_type}</span>}
                                        <span className={`ml-auto rounded-full px-2.5 py-0.5 font-bold uppercase tracking-wider text-[10px] ${r.status === "approved" ? "bg-green-100 text-green-700" : r.status === "rejected" ? "bg-red-100 text-red-700" : "bg-gold/20 text-yellow-700"}`} data-testid={`review-status-${r.id}`}>{r.status}</span>
                                    </div>
                                    <p className="mt-2 text-sm text-stone-700">"{r.text}"</p>
                                    <div className="mt-3 flex gap-2">
                                        {r.status !== "approved" && (
                                            <button onClick={() => reviewAction(r.id, "approved")} data-testid={`review-approve-${r.id}`} className="px-4 py-1.5 rounded-full bg-navy text-white text-[11px] font-display font-black uppercase tracking-wider hover:bg-navy-light">Approve</button>
                                        )}
                                        {r.status !== "rejected" && (
                                            <button onClick={() => reviewAction(r.id, "rejected")} data-testid={`review-reject-${r.id}`} className="px-4 py-1.5 rounded-full border border-stone-300 text-stone-600 text-[11px] font-display font-black uppercase tracking-wider hover:bg-stone-100">Reject</button>
                                        )}
                                        <button onClick={() => reviewAction(r.id, "delete")} data-testid={`review-delete-${r.id}`} className="px-4 py-1.5 rounded-full border border-red-200 text-red-600 text-[11px] font-display font-black uppercase tracking-wider hover:bg-red-50">Delete</button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <section className="bg-white rounded-2xl border border-stone-300 p-6" data-testid="stories-manager">
                    <h2 className="font-display font-black text-lg text-navy">Real Trip Stories <span className="text-saffron">({stories.length})</span></h2>
                    <p className="text-xs text-stone-500 mt-1">Stories you add here replace the sample placeholders on the homepage.</p>
                    <form onSubmit={submitStory} className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="story-form">
                        <input data-testid="story-title-input" className="rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-saffron" placeholder="Title — e.g. College Trip — Goa *" value={storyForm.title} onChange={(e) => setStoryForm((f) => ({ ...f, title: e.target.value }))} />
                        <input data-testid="story-travellers-input" className="rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-saffron" placeholder="Travellers — e.g. 120 Travellers" value={storyForm.travellers} onChange={(e) => setStoryForm((f) => ({ ...f, travellers: e.target.value }))} />
                        <textarea data-testid="story-quote-input" className="md:col-span-2 rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-saffron" rows={2} placeholder="Client quote *" value={storyForm.quote} onChange={(e) => setStoryForm((f) => ({ ...f, quote: e.target.value }))} />
                        <input data-testid="story-name-input" className="rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-saffron" placeholder="Client name *" value={storyForm.name} onChange={(e) => setStoryForm((f) => ({ ...f, name: e.target.value }))} />
                        <input data-testid="story-org-input" className="rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-saffron" placeholder="Organisation — e.g. St. Mary's School" value={storyForm.org} onChange={(e) => setStoryForm((f) => ({ ...f, org: e.target.value }))} />
                        <input data-testid="story-image-input" className="md:col-span-2 rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-saffron" placeholder="Photo URL (https://…) — optional" value={storyForm.image} onChange={(e) => setStoryForm((f) => ({ ...f, image: e.target.value }))} />
                        <button type="submit" disabled={addingStory} data-testid="story-add-btn" className="md:col-span-2 self-start px-6 py-2.5 rounded-full bg-saffron text-white text-xs font-display font-black uppercase tracking-wider hover:bg-saffron-dark disabled:opacity-60">
                            {addingStory ? "Publishing…" : "Publish Story"}
                        </button>
                    </form>
                    {stories.length > 0 && (
                        <ul className="mt-5 space-y-2">
                            {stories.map((s) => (
                                <li key={s.id} className="flex items-center justify-between gap-4 rounded-xl border border-stone-200 px-4 py-2.5 text-sm" data-testid={`story-row-${s.id}`}>
                                    <span className="font-bold text-navy">{s.title} <span className="font-normal text-stone-500">— {s.name}</span></span>
                                    <button onClick={() => delStory(s.id)} data-testid={`story-delete-${s.id}`} className="text-stone-400 hover:text-red-600" aria-label="Delete story"><Trash2 className="h-4 w-4" /></button>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <section className="bg-white rounded-2xl border border-stone-300 p-6" data-testid="leads-panel">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <h2 className="font-display font-black text-lg text-navy">Trip Enquiries <span className="text-saffron">({leads.length})</span></h2>
                        <button
                            onClick={async () => { try { await exportLeadsCsv(token); toast.success("CSV downloaded"); } catch { toast.error("Export failed"); } }}
                            data-testid="export-csv-btn"
                            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-navy text-white text-xs font-display font-black uppercase tracking-wider hover:bg-navy-light"
                        >
                            Export CSV
                        </button>
                    </div>
                    {loading ? (
                        <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div>
                    ) : leads.length === 0 ? (
                        <p className="py-10 text-sm text-stone-500" data-testid="leads-empty">No enquiries yet. Share your site and they'll land here.</p>
                    ) : (
                        <div className="mt-4 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b border-stone-200">
                                        <th className="py-2 pr-4">Date</th><th className="py-2 pr-4">Name</th><th className="py-2 pr-4">Phone</th><th className="py-2 pr-4">Trip</th><th className="py-2 pr-4">Group</th><th className="py-2 pr-4">Destination</th><th className="py-2 pr-4">Source</th><th className="py-2 pr-4">Status</th><th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {leads.map((l) => (
                                        <tr key={l.id} className="border-b border-stone-100" data-testid={`lead-row-${l.id}`}>
                                            <td className="py-3 pr-4 whitespace-nowrap text-stone-500">{new Date(l.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</td>
                                            <td className="py-3 pr-4 font-bold text-navy whitespace-nowrap">{l.name}</td>
                                            <td className="py-3 pr-4 whitespace-nowrap"><a href={`tel:${l.phone}`} className="text-saffron hover:underline">{l.phone}</a></td>
                                            <td className="py-3 pr-4 whitespace-nowrap">{l.trip_type || "—"}</td>
                                            <td className="py-3 pr-4 whitespace-nowrap">{l.group_size || "—"}</td>
                                            <td className="py-3 pr-4 whitespace-nowrap">{l.destination || "—"}</td>
                                            <td className="py-3 pr-4 whitespace-nowrap text-stone-500">{l.source}
                                                {l.payment?.status === "paid" && (
                                                    <span className="ml-1.5 rounded-full bg-green-600 text-white px-2 py-0.5 text-[10px] font-black uppercase tracking-wider" data-testid={`lead-paid-${l.id}`}>Paid {inr(l.payment.amount)}</span>
                                                )}
                                                {l.associate_code && (
                                                    <span className="ml-1.5 rounded-full bg-navy/10 text-navy px-2 py-0.5 text-[10px] font-black uppercase tracking-wider" data-testid={`lead-ref-${l.id}`}>via {l.associate_code}</span>
                                                )}
                                            </td>
                                            <td className="py-3 pr-4">
                                                <select value={l.status} onChange={(e) => changeStatus(l.id, e.target.value)} data-testid={`lead-status-${l.id}`} className={`rounded-full px-2.5 py-1 text-xs font-bold border-0 outline-none ${STATUS_STYLES[l.status] || ""}`}>
                                                    <option value="new">New</option>
                                                    <option value="contacted">Contacted</option>
                                                    <option value="closed">Closed</option>
                                                </select>
                                            </td>
                                            <td className="py-3"><button onClick={() => del(l.id)} data-testid={`lead-delete-${l.id}`} className="text-stone-400 hover:text-red-600" aria-label="Delete lead"><Trash2 className="h-4 w-4" /></button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
                </>
                ) : view === "packages" ? (
                    <PackageManager token={token} />
                ) : view === "sales" ? (
                    <SalesToursManager token={token} access={access} />
                ) : view === "flights" ? (
                    <FlightsManager token={token} />
                ) : view === "social" ? (
                    <TabErrorBoundary>
                        <Suspense fallback={<div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-saffron" /></div>}>
                            <SocialMediaManager token={token} />
                        </Suspense>
                    </TabErrorBoundary>
                ) : view === "reels" ? (
                    <ReelsModeration token={token} />
                ) : view === "feedback" ? (
                    <FeedbackManager token={token} />
                ) : view === "inbox" ? (
                    <WhatsAppInbox token={token} />
                ) : view === "associates" ? (
                    <AssociatesManager token={token} />
                ) : view === "access" && access?.is_super ? (
                    <AccessManager token={token} />
                ) : (
                    <FareManager token={token} />
                )}
            </main>
        </div>
    );
}
